import {
    MutableRefObject,
    useEffect,
    useLayoutEffect,
    useMemo,
    useRef,
    useState,
} from "react";

import { ColumnPinningState, TableState, Updater } from "@tanstack/react-table";
import { useNavigate, useLocation } from "react-router-dom";
import { Returns, useTableSearchParams } from "tanstack-table-search-params";
import { encodeSorting } from "tanstack-table-search-params/encoder-decoder";
import { z } from "zod";

import { useReplaceSearchParams } from "../../hooks";
import { emitNotification } from "../../utils/notification";

import {
    decodeValuesWithCodecMap,
    encodeColumnFilters,
} from "./codecs/columnFiltersCodec";
import {
    decodeColumnOrder,
    encodeColumnOrder,
} from "./codecs/columnOrderCodec";
import { encodeColumnPinning } from "./codecs/columnPinningCodec";
import {
    decodeColumnVisibility,
    encodeColumnVisibility,
} from "./codecs/columnVisibilityCodec";
import {
    DataParamsApi,
    DataParamsCodecMap,
    deleteDataParams,
} from "./dataParams";
import {
    columnBaselineFingerprint,
    COLUMN_ORDER_PARAM_NAME,
    COLUMN_PINNING_PARAM_NAME,
    COLUMN_VISIBILITY_PARAM_NAME,
    isBaselineStale,
    readInitialColumnPinning,
    withBaselineFingerprint,
    TABLE_BASELINE_PARAM_NAME,
} from "./positionalColumnParams";
import { PresetSearchParamsCustomDefaultValues } from "./types";
import { useDataParams } from "./useDataParams";
import { useTableStateStorage } from "./useTableStateStorage";

// The lib's default URL param name for column filters.
export const COLUMN_FILTERS_PARAM_NAME = "columnFilters";

type PersistedState = Pick<
    TableState,
    | "sorting"
    | "columnFilters"
    | "columnVisibility"
    | "columnOrder"
    | "columnPinning"
    | "pagination"
>;
export interface UsePresetTableSearchParamsReturns<
    M extends DataParamsCodecMap,
> {
    state: Partial<PersistedState>;
    onStateChange: (updater: Updater<TableState>) => void;
    initialState: Partial<PersistedState>;
    /**
     * Non-column data-source params (e.g. an archived flag) persisted on the
     * same `tableId` through the same URL + localStorage machinery. Empty when
     * no `dataParamsCodecMap` was passed.
     */
    dataParams: DataParamsApi<M>;
    /**
     * Resets column filters and all data params in a single navigation. Use as
     * the "clear all" handler when a table has data params
     */
    clearAllFilters: () => void;
}
/**
 * Custom hook for managing table search parameters with URL synchronization and local storage persistence
 * @param tableId - Unique identifier for the table, used for local storage key
 * @param defaultValues - Default values for sorting and column visibility
 * @param columnFiltersCodecMap - Map of column filter codecs for encoding/decoding filter values
 * @param dataParamsCodecMap - Data-source filters (a codec per URL param name);
 * defaults from `defaultValues.dataParams`. Values come back under `dataParams`.
 * @returns Object containing table state and change handlers
 */
export function usePresetTableSearchParams<
    M extends DataParamsCodecMap = Record<string, never>,
>(
    tableId: string,
    defaultValues: PresetSearchParamsCustomDefaultValues,
    columnFiltersCodecMap: Record<string, z.ZodCodec>,
    notificationLabel: string,
    onError: (error: unknown) => void,
    dataParamsCodecMap: M = {} as M
): UsePresetTableSearchParamsReturns<M> {
    const { pathname, search, hash } = useLocation();
    const navigate = useNavigate();

    const [, setLocalStorageState] =
        useTableStateStorage<Record<string, any>>(tableId);
    const erroredRef = useRef<Record<string, z.ZodError> | undefined>(
        undefined
    );

    // A stale (or missing) fingerprint means the positional params were encoded
    // against a different default order: ignore on read, strip from URL below.
    const baselineFingerprint = columnBaselineFingerprint(
        defaultValues.columnOrder
    );
    const baselineStale = isBaselineStale(search, baselineFingerprint);

    // The lib doesn't manage pinning, so we own its param: seed once from the URL
    // (or default), then keep it in refs `replace`/`onStateChange` read below.
    const [initialColumnPinning] = useState<ColumnPinningState | undefined>(
        () =>
            baselineStale
                ? defaultValues.columnPinning
                : readInitialColumnPinning(search, defaultValues)
    );
    const columnPinningRef = useRef<ColumnPinningState | undefined>(
        initialColumnPinning
    );
    // Reactive copy feeding the table's `state`. Must stay concrete: `undefined`
    // clobbers tanstack's `{ left: [], right: [] }` default and breaks core reads.
    const [columnPinning, setColumnPinning] = useState<ColumnPinningState>(
        initialColumnPinning ?? { left: [], right: [] }
    );
    const pinningEncodedRef = useRef<string | undefined>(
        defaultValues.columnOrder
            ? (encodeColumnPinning(
                  initialColumnPinning ?? {},
                  defaultValues.columnOrder,
                  defaultValues.columnPinning
              ) ?? undefined)
            : undefined
    );

    // The lib only carries forward the pinning value already in the URL, so
    // re-stamp it on every navigation
    const replace = (url: string) => {
        const [path, queryString = ""] = url.split("?");
        const params = new URLSearchParams(queryString);
        const encoded = pinningEncodedRef.current;
        if (encoded) {
            params.set(COLUMN_PINNING_PARAM_NAME, encoded);
        } else {
            params.delete(COLUMN_PINNING_PARAM_NAME);
        }
        const nextQuery = withBaselineFingerprint(
            params,
            baselineFingerprint
        ).toString();
        navigate(nextQuery ? `${path}?${nextQuery}${hash}` : path, {
            replace: true,
        });
    };

    const stateAndOnChanges = useTableSearchParams(
        {
            query: new URLSearchParams(search),
            pathname,
            replace,
        },
        {
            encoders: {
                columnFilters: (filters) =>
                    encodeColumnFilters(
                        filters,
                        columnFiltersCodecMap,
                        onError
                    ),
                sorting: (sorting) => {
                    const encoded = encodeSorting(sorting, {
                        defaultValue: defaultValues.sorting,
                    });
                    return encoded ? { sorting: encoded } : {};
                },
                columnVisibility: (visibility) => {
                    if (!defaultValues.columnOrder) {
                        return {};
                    }
                    const encoded = encodeColumnVisibility(
                        visibility ?? {},
                        defaultValues.columnOrder,
                        defaultValues.columnVisibility
                    );
                    return encoded
                        ? { [COLUMN_VISIBILITY_PARAM_NAME]: encoded }
                        : {};
                },
                columnOrder: (order) => {
                    if (!defaultValues.columnOrder) {
                        return {};
                    }
                    const encoded = encodeColumnOrder(
                        order ?? [],
                        defaultValues.columnOrder
                    );
                    return encoded
                        ? { [COLUMN_ORDER_PARAM_NAME]: encoded }
                        : {};
                },
            },
            decoders: {
                columnFilters: (params) => {
                    const encodedFilters = params.columnFilters;
                    if (!encodedFilters) {
                        return [];
                    }
                    const { decodedFilters, erroredFilters } =
                        decodeValuesWithCodecMap(
                            encodedFilters,
                            columnFiltersCodecMap
                        );
                    // Picked up by an effect below
                    if (Object.keys(erroredFilters).length > 0) {
                        erroredRef.current = erroredFilters;
                    }

                    return decodedFilters;
                },
                columnOrder: (params) => {
                    const encoded = params[COLUMN_ORDER_PARAM_NAME];
                    if (
                        baselineStale ||
                        !defaultValues.columnOrder ||
                        typeof encoded !== "string"
                    ) {
                        return [];
                    }
                    const decoded = decodeColumnOrder(
                        encoded,
                        defaultValues.columnOrder
                    );
                    return decoded ?? [];
                },
                columnVisibility: (params) => {
                    const encoded = params[COLUMN_VISIBILITY_PARAM_NAME];
                    if (
                        baselineStale ||
                        !defaultValues.columnOrder ||
                        typeof encoded !== "string"
                    ) {
                        return defaultValues.columnVisibility ?? {};
                    }
                    const decoded = decodeColumnVisibility(
                        encoded,
                        defaultValues.columnOrder,
                        defaultValues.columnVisibility
                    );
                    return decoded ?? defaultValues.columnVisibility ?? {};
                },
            },
            defaultValues: {
                // Add a per-table override with this as fallback if ever needed
                sorting: defaultValues.sorting,
                columnVisibility: defaultValues.columnVisibility,
                columnOrder: defaultValues.columnOrder,
            },
            // Prevent row selections from pushing to search params
            enabled: { rowSelection: false },
        }
    );

    // Syncs localStorage and strips invalid params; ideally folded into the
    // encoder/decoder handlers if we ever fork the lib
    useSyncFilterStateEffects(
        erroredRef,
        columnFiltersCodecMap,
        stateAndOnChanges,
        tableId,
        notificationLabel,
        onError
    );

    const trackedState = useMemo<Partial<PersistedState>>(
        () => ({
            sorting: stateAndOnChanges.state.sorting,
            columnFilters: stateAndOnChanges.state.columnFilters,
            columnVisibility: stateAndOnChanges.state.columnVisibility,
            columnOrder: stateAndOnChanges.state.columnOrder,
            // Always concrete (see `useState` above), never `undefined`
            columnPinning,
        }),
        [
            stateAndOnChanges.state.sorting,
            stateAndOnChanges.state.columnFilters,
            stateAndOnChanges.state.columnVisibility,
            stateAndOnChanges.state.columnOrder,
            columnPinning,
        ]
    );

    // localStorage sink; URL persistence is handled atomically by the lib
    useEffect(() => {
        setLocalStorageState((prev) => ({ ...prev, ...trackedState }));
    }, [trackedState, setLocalStorageState]);

    // Seed pinning into localStorage on mount so deep links carry it. Runs after
    // the initial-sync effect, which replaces rather than merges the stored object.
    useEffect(() => {
        setLocalStorageState((prev) => ({
            ...prev,
            columnPinning: columnPinningRef.current,
        }));
    }, [setLocalStorageState]);

    // Decoders already fell back to defaults; strip the stale positional params
    // (and fingerprint) from the URL, flipping `baselineStale` false for the session
    useEffect(() => {
        if (!baselineStale) {
            return;
        }
        const params = new URLSearchParams(search);
        params.delete(COLUMN_ORDER_PARAM_NAME);
        params.delete(COLUMN_VISIBILITY_PARAM_NAME);
        params.delete(COLUMN_PINNING_PARAM_NAME);
        params.delete(TABLE_BASELINE_PARAM_NAME);
        const nextQuery = params.toString();
        navigate(nextQuery ? `${pathname}?${nextQuery}${hash}` : pathname, {
            replace: true,
        });
    }, [baselineStale, search, pathname, hash, navigate]);

    // Shadow `columnPinning` from each updater (the lib ignores it): derive the
    // next value, refresh the URL param ref, and mirror to localStorage. It
    // always changes alongside `columnOrder`, so the lib navigates in lockstep.
    const onStateChange = (updater: Updater<TableState>) => {
        if (defaultValues.columnOrder) {
            const prevPinning = columnPinningRef.current ?? {
                left: [],
                right: [],
            };
            const nextState =
                typeof updater === "function"
                    ? updater({
                          ...stateAndOnChanges.state,
                          columnPinning: prevPinning,
                      } as TableState)
                    : updater;
            const nextPinning = nextState.columnPinning ?? prevPinning;
            columnPinningRef.current = nextPinning;
            setColumnPinning(nextPinning);
            pinningEncodedRef.current =
                encodeColumnPinning(
                    nextPinning,
                    defaultValues.columnOrder,
                    defaultValues.columnPinning
                ) ?? undefined;
            setLocalStorageState((prev) => ({
                ...prev,
                columnPinning: nextPinning,
            }));
        }
        stateAndOnChanges.onStateChange(updater);
    };

    // Data-source params ride on the same `tableId`, composing with the table
    // state by only touching their own URL keys
    const dataParams = useDataParams(
        tableId,
        dataParamsCodecMap,
        defaultValues.dataParams ?? {}
    );
    const replaceSearchParams = useReplaceSearchParams();

    // Clears column filters and every data param in ONE navigation
    const clearAllFilters = () => {
        replaceSearchParams((params) => {
            params.delete(COLUMN_FILTERS_PARAM_NAME);
            deleteDataParams(params, dataParamsCodecMap);
        });
    };

    return {
        state: trackedState,
        onStateChange,
        dataParams,
        clearAllFilters,
        initialState: {
            columnVisibility: defaultValues.columnVisibility,
            columnOrder: defaultValues.columnOrder,
            // The *default* pinning — the reset target read via `initialState`
            // (the live value rides in `state`). Set only when defined; an
            // `undefined` would clobber tanstack's default and break core reads.
            ...(defaultValues.columnPinning && {
                columnPinning: defaultValues.columnPinning,
            }),
        },
    };
}

function useSyncFilterStateEffects(
    erroredRef: MutableRefObject<Record<string, z.ZodError> | undefined>,
    columnFiltersCodecMap: Record<string, z.ZodCodec>,
    stateAndOnChanges: Returns,
    tableId: string,
    notificationLabel: string,
    onError?: (error: unknown) => void
) {
    const { search } = useLocation();
    const navigate = useNavigate();
    const [, setLocalStorageState] =
        useTableStateStorage<Record<string, any>>(tableId);

    useLayoutEffect(() => {
        if (!erroredRef.current) {
            return;
        }

        const searchObj = new URLSearchParams(search);
        const pathname = window.location.pathname;

        const validFilters = stateAndOnChanges.state.columnFilters?.filter(
            (filter) =>
                !erroredRef.current ||
                !Object.keys(erroredRef.current).includes(filter.id)
        );

        emitNotification("warning", notificationLabel, {
            duration: "DOUBLE",
        });

        if (!validFilters || validFilters.length === 0) {
            searchObj.delete(COLUMN_FILTERS_PARAM_NAME);
        } else {
            const newFilters = encodeColumnFilters(
                validFilters,
                columnFiltersCodecMap,
                onError
            );
            if (newFilters.columnFilters) {
                searchObj.set(
                    COLUMN_FILTERS_PARAM_NAME,
                    String(newFilters.columnFilters)
                );
            } else {
                searchObj.delete(COLUMN_FILTERS_PARAM_NAME);
            }
        }

        navigate({ pathname, search: searchObj.toString() }, { replace: true });

        erroredRef.current = undefined;
    }, [
        navigate,
        search,
        stateAndOnChanges.state.columnFilters,
        columnFiltersCodecMap,
        erroredRef,
        notificationLabel,
        onError,
    ]);

    const initialSyncDone = useRef(false);
    // Sync URL state to localStorage on first mount.
    useEffect(() => {
        if (initialSyncDone.current === true) {
            return;
        }
        const currentState = {
            sorting: stateAndOnChanges.state.sorting,
            columnFilters: stateAndOnChanges.state.columnFilters,
            columnVisibility: stateAndOnChanges.state.columnVisibility,
            columnOrder: stateAndOnChanges.state.columnOrder,
        };

        setLocalStorageState(() => currentState);
        initialSyncDone.current = true;
    }, [stateAndOnChanges, setLocalStorageState]);
}
